import { useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { useModalA11y } from "../hooks/useModalA11y.js";

/**
 * Generic post-flow result modal used for success / error states.
 *
 * @param {object} props
 * @param {boolean} props.open
 * @param {"success"|"error"} [props.type="success"]
 * @param {string} props.title
 * @param {string} props.message
 * @param {{label: string, to?: string, onClick?: () => void}} [props.primaryAction]
 *        Optional CTA. If `to` is set the action renders as a Router link.
 *        Otherwise it falls through to onClick or the close handler.
 * @param {() => void} props.onClose
 */
export default function ResultModal({ open, type = "success", title, message, primaryAction, onClose }) {
    // Shared a11y plumbing — Escape, focus trap, return focus to the
    // element that opened the modal. See useModalA11y for the contract.
    const dialogRef = useRef(null);
    useModalA11y({ open, onClose, containerRef: dialogRef });

    if (!open) return null;

    const isError = type === "error";

    return createPortal(
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center bg-[#0E2C19]/55 backdrop-blur-sm p-4"
            onClick={onClose}
            role="presentation"
        >
            <div
                ref={dialogRef}
                className="w-full max-w-md overflow-hidden rounded-[14px] bg-[#10281b] ring-1 ring-[#bfb67b] shadow-xl"
                onClick={(event) => event.stopPropagation()}
                role="dialog"
                aria-modal="true"
                aria-labelledby="result-modal-title"
            >
                {/* Banner — darker inset bar; the badge colour disambiguates */}
                <div className="bg-[#162e1f] border-b border-[#bfb67b]/30 px-5 py-4 flex items-center gap-3">
                    {isError ? <ErrorBadge /> : <SuccessBadge />}
                    <div className="min-w-0 flex-1">
                        <div className="text-[9px] font-semibold uppercase tracking-widest text-[#FBC206]">
                            {isError ? "Action Failed" : "Action Completed"}
                        </div>
                        <h3
                            id="result-modal-title"
                            className="mt-0.5 font-manrope text-[15px] font-bold text-[#e6c985]"
                        >
                            {title}
                        </h3>
                    </div>
                </div>

                <div className="p-5">
                    <p className="text-[12px] leading-relaxed text-white/85 break-words">
                        {message}
                    </p>

                    <div className="mt-5 flex flex-wrap justify-end gap-2">
                        <button
                            className="inline-flex items-center justify-center rounded-md bg-white/10 px-4 py-2 text-[12px] font-semibold text-white ring-1 ring-[#bfb67b] hover:bg-white/20 transition-colors"
                            onClick={onClose}
                        >
                            Close
                        </button>
                        {primaryAction ? (
                            primaryAction.to ? (
                                <Link
                                    to={primaryAction.to}
                                    onClick={onClose}
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
                                >
                                    {primaryAction.label}
                                    <ChevronRightSmall />
                                </Link>
                            ) : (
                                <button
                                    className="inline-flex items-center justify-center gap-2 rounded-md bg-[#FBC206] px-4 py-2 text-[12px] font-semibold text-[#10281b] hover:-translate-y-0.5 hover:shadow-md transition-all"
                                    onClick={() => {
                                        primaryAction.onClick?.();
                                        onClose?.();
                                    }}
                                >
                                    {primaryAction.label}
                                    <ChevronRightSmall />
                                </button>
                            )
                        ) : null}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}

function SuccessBadge() {
    return (
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FBC206]">
            <svg
                className="h-5 w-5 text-[#1D3E29]"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <polyline points="4 11 8 15 16 6" />
            </svg>
        </span>
    );
}

function ErrorBadge() {
    return (
        <span className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#FDE2E2] ring-1 ring-[#C93030]/40">
            <svg
                className="h-5 w-5 text-[#C93030]"
                viewBox="0 0 20 20"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
            >
                <line x1="5" y1="5" x2="15" y2="15" />
                <line x1="15" y1="5" x2="5" y2="15" />
            </svg>
        </span>
    );
}

function ChevronRightSmall() {
    return (
        <svg
            className="h-3 w-3"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            <polyline points="7 4 13 10 7 16" />
        </svg>
    );
}
